import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { ondiApi } from "../lib/ondi";

const OndiContext = createContext(null);

const HISTORY_KEY = "queonda.ondi.history";
const POSITION_KEY = "queonda.ondi.position";

function loadHistory() {
  try {
    const s = localStorage.getItem(HISTORY_KEY);
    if (!s) return [];
    const arr = JSON.parse(s);
    if (!Array.isArray(arr)) return [];
    return arr.slice(-20);
  } catch (_e) {
    return [];
  }
}

function saveHistory(h) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(h.slice(-20)));
  } catch (_e) {
    /* ignore */
  }
}

export const OndiProvider = ({ children }) => {
  const { status, user } = useAuth();
  const [panelOpen, setPanelOpen] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [drivingActive, setDrivingActive] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [history, setHistory] = useState(() => loadHistory());
  const [greeting, setGreeting] = useState(null);
  const [greetedThisSession, setGreetedThisSession] = useState(false);
  const [position, setPosition] = useState(() => {
    try {
      const raw = localStorage.getItem(POSITION_KEY);
      if (!raw) return null;
      const p = JSON.parse(raw);
      if (typeof p?.x === "number" && typeof p?.y === "number") return p;
    } catch (_e) {
      /* ignore */
    }
    return null;
  });
  const initialGreetingSentRef = useRef(false);

  // Reset per-session flags when auth changes
  useEffect(() => {
    if (status !== "authed") {
      setPanelOpen(false);
      setTourActive(false);
      setDrivingActive(false);
      setThinking(false);
      setGreeting(null);
      setGreetedThisSession(false);
      initialGreetingSentRef.current = false;
    }
  }, [status]);

  // On login, fetch greeting snapshot + optionally auto-open panel
  useEffect(() => {
    if (status !== "authed" || !user) return;
    if (initialGreetingSentRef.current) return;
    initialGreetingSentRef.current = true;
    ondiApi
      .greeting()
      .then((data) => {
        setGreeting(data);
        // Build a first-time or returning greeting message locally (no LLM call needed)
        const firstName = user.display_name?.split(" ")[0] || "amig@";
        const isNew = !data.has_seen_tour;
        const text = isNew
          ? `¡Qué onda, ${firstName}! Soy ONDI, tu guía en Qué Onda Chat. ¿Le echamos un tour rápido para que conozcas todo?`
          : buildReturningGreeting(firstName, data);
        setHistory((prev) => {
          if (prev.length && prev[prev.length - 1].role === "assistant") return prev;
          const next = [...prev, { role: "assistant", content: text, __greeting: true }];
          saveHistory(next);
          return next;
        });
        if (isNew) {
          // Auto-open only for first-time users
          setPanelOpen(true);
        } else {
          // Auto-open a discreet bubble too — feels alive, but only if unread > 0 (relevant)
          if ((data.unread_count || 0) > 0) setPanelOpen(true);
        }
      })
      .catch(() => {});
  }, [status, user]);

  const sendMessage = useCallback(
    async (text) => {
      const clean = text.trim();
      if (!clean) return;
      const userTurn = { role: "user", content: clean };
      setHistory((prev) => {
        const next = [...prev, userTurn];
        saveHistory(next);
        return next;
      });
      setThinking(true);
      try {
        const shortHistory = history
          .filter((m) => !m.__greeting)
          .slice(-8)
          .map(({ role, content }) => ({ role, content }));
        const data = await ondiApi.chat(clean, shortHistory);
        const reply = { role: "assistant", content: data.reply };
        setHistory((prev) => {
          const next = [...prev, reply];
          saveHistory(next);
          return next;
        });
      } catch (_e) {
        const errMsg = {
          role: "assistant",
          content: "Se me trabó un cable interno. Vuelve a intentarlo, porfa.",
        };
        setHistory((prev) => {
          const next = [...prev, errMsg];
          saveHistory(next);
          return next;
        });
      } finally {
        setThinking(false);
      }
    },
    [history],
  );

  const openPanel = useCallback(() => setPanelOpen(true), []);
  const closePanel = useCallback(() => setPanelOpen(false), []);
  const togglePanel = useCallback(() => setPanelOpen((v) => !v), []);
  const startTour = useCallback(() => {
    setTourActive(true);
    setPanelOpen(false);
  }, []);
  const finishTour = useCallback(async () => {
    setTourActive(false);
    try {
      await ondiApi.tourComplete();
    } catch (_e) {
      /* ignore */
    }
    setGreeting((g) => (g ? { ...g, has_seen_tour: true } : g));
  }, []);
  const openDriving = useCallback(() => {
    setPanelOpen(false);
    setTourActive(false);
    setDrivingActive(true);
  }, []);
  const closeDriving = useCallback(() => {
    setDrivingActive(false);
  }, []);
  const clearHistory = useCallback(() => {
    setHistory([]);
    saveHistory([]);
  }, []);
  const savePosition = useCallback((p) => {
    setPosition(p);
    try {
      localStorage.setItem(POSITION_KEY, JSON.stringify(p));
    } catch (_e) {
      /* ignore */
    }
  }, []);

  const value = useMemo(
    () => ({
      panelOpen,
      openPanel,
      closePanel,
      togglePanel,
      tourActive,
      startTour,
      finishTour,
      drivingActive,
      openDriving,
      closeDriving,
      thinking,
      history,
      sendMessage,
      greeting,
      position,
      savePosition,
      clearHistory,
    }),
    [
      panelOpen,
      openPanel,
      closePanel,
      togglePanel,
      tourActive,
      startTour,
      finishTour,
      drivingActive,
      openDriving,
      closeDriving,
      thinking,
      history,
      sendMessage,
      greeting,
      position,
      savePosition,
      clearHistory,
    ],
  );

  return <OndiContext.Provider value={value}>{children}</OndiContext.Provider>;
};

function buildReturningGreeting(firstName, data) {
  const u = data.unread_count || 0;
  const p = data.active_pulses || 0;
  const parts = [`¡Qué onda de nuevo, ${firstName}!`];
  if (u > 0) parts.push(`Tienes ${u} mensaje${u === 1 ? "" : "s"} sin leer`);
  else parts.push("Bandeja limpia");
  if (p > 0) parts.push(`y ${p} pulso${p === 1 ? "" : "s"} activo${p === 1 ? "" : "s"} en el feed`);
  return parts.join(" ") + ". ¿En qué te ayudo?";
}

export function useOndi() {
  const ctx = useContext(OndiContext);
  if (!ctx) throw new Error("useOndi debe usarse dentro de OndiProvider");
  return ctx;
}
